
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTickets } from '@/hooks/useTickets';
import { Ticket } from '@/types/crm';

interface TicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket?: Ticket | null;
}

export const TicketModal: React.FC<TicketModalProps> = ({
  isOpen,
  onClose,
  ticket
}) => {
  const { createTicket, updateTicket } = useTickets();
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    priority: '',
    status: 'open',
    source: '',
    sla_due_at: ''
  });
  const [description, setDescription] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ticket) {
      setFormData({
        title: ticket.title || '',
        type: ticket.type || '',
        priority: ticket.priority || '',
        status: ticket.status || 'open',
        source: ticket.source || '',
        sla_due_at: ticket.sla_due_at?.split('T')[0] || ''
      });
    } else {
      setFormData({
        title: '',
        type: '',
        priority: '',
        status: 'open',
        source: '',
        sla_due_at: ''
      });
      setDescription('');
      setResponse('');
    }
  }, [ticket]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const ticketData = {
        ...formData,
        sla_due_at: formData.sla_due_at || null
      };

      if (ticket) {
        await updateTicket(ticket.id, ticketData);
      } else {
        await createTicket(ticketData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {ticket ? 'Ticket Bewerken' : 'Nieuw Ticket'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Ticket Details</TabsTrigger>
              <TabsTrigger value="response">Beantwoorden</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={formData.type} onValueChange={(val) => setFormData(prev => ({ ...prev, type: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer type..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="crm">CRM</SelectItem>
                      <SelectItem value="platform">Platform</SelectItem>
                      <SelectItem value="energy">Energy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Prioriteit</Label>
                  <Select value={formData.priority} onValueChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer prioriteit..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P3">P3 - Laag</SelectItem>
                      <SelectItem value="P2">P2 - Normaal</SelectItem>
                      <SelectItem value="P1">P1 - Hoog</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Behandeling</SelectItem>
                      <SelectItem value="waiting_customer">Wacht op Klant</SelectItem>
                      <SelectItem value="resolved">Opgelost</SelectItem>
                      <SelectItem value="closed">Gesloten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">Bron</Label>
                  <Select value={formData.source} onValueChange={(val) => setFormData(prev => ({ ...prev, source: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer bron..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="form">Formulier</SelectItem>
                      <SelectItem value="phone">Telefoon</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sla_due_at">SLA Vervaldatum</Label>
                <Input
                  id="sla_due_at"
                  type="date"
                  value={formData.sla_due_at}
                  onChange={(e) => setFormData(prev => ({ ...prev, sla_due_at: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Omschrijving</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  placeholder="Beschrijf het probleem of verzoek..."
                />
              </div>
            </TabsContent>

            <TabsContent value="response" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="response">Antwoord</Label>
                <Textarea
                  id="response"
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  rows={8}
                  placeholder="Typ je antwoord hier..."
                />
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <h4 className="font-medium mb-2">Berichtgeschiedenis</h4>
                <p className="text-sm text-muted-foreground">
                  Berichtgeschiedenis wordt hier weergegeven zodra deze functionaliteit is gekoppeld.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading || !formData.title}>
              {loading ? 'Opslaan...' : ticket ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
